import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import moment from "moment";
import { toast } from "react-toastify";

import { getReportRangeErrorMessage } from "src/utils/report/reportRange.util";

import { PageLayout } from "../../layouts/PageLayout";
import { CardLayout, EmptyState, InputField, PageHeader } from "../../components/v3";
import { DailyReportResults } from "../../components/report";
import { AnalyticsService } from "../../services";

export const DailyReport = () => {
  const [selectedDate, setSelectedDate] = useState(moment().format("YYYY-MM-DD"));
  const [loading, setLoading] = useState(false);
  const [reportFilters, setReportFilters] = useState(null);
  const [reportData, setReportData] = useState({});
  const [paginationState, setPaginationState] = useState({ page: 0, pageSize: 10 });

  const fetchData = async (filters) => {
    try {
      const { data } = await AnalyticsService.getDailyReport(filters);
      setReportData(data);
    } catch (error) {
      console.error("Error fetching daily report data:", error);
      toast.error((await getReportRangeErrorMessage(error)) ?? "Failed to load daily report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedDate) {
      setReportData({});
      setReportFilters(null);
      setPaginationState({ page: 0, pageSize: 10 });
      return;
    }

    const filters = {
      fromDate: moment(selectedDate).startOf("day").toISOString(),
      toDate: moment(selectedDate).endOf("day").toISOString(),
    };

    setLoading(true);
    setReportData({});
    setReportFilters(filters);
    setPaginationState({ page: 0, pageSize: 10 });
    fetchData({
      ...filters,
      page: 1,
      limit: 10,
    });
  }, [selectedDate]);

  const handlePaginationChange = (model) => {
    if (!reportFilters) return;

    setLoading(true);
    setPaginationState(model);
    fetchData({
      ...reportFilters,
      page: model.page + 1,
      limit: model.pageSize,
    });
  };

  return (
    <PageLayout>
      {/* Header section */}
      <Box sx={{ position: "relative", zIndex: 1 }}>
        <PageHeader title="Daily Report" />
      </Box>

      <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
        <CardLayout>
          {/* Date input */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, pr: 1 }}>
            <Box sx={{ width: 220 }}>
              <InputField
                label="Select Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                size="small"
                themeColor="#674BF5"
                fullWidth
              />
            </Box>
          </Box>

          {/* Content area */}
          {!selectedDate ? (
            <EmptyState title="No Date Selected" subtitle="Please select a date to view the report" />
          ) : loading ? (
            <DailyReportResults
              data={reportData}
              loading={loading}
              handlePaginationChange={handlePaginationChange}
              reportFilters={reportFilters}
            />
          ) : reportData?.count === undefined ? (
            <EmptyState
              title="Unable to load report"
              subtitle="There was a problem loading the daily report. Please try again."
            />
          ) : reportData?.count > 0 ? (
            <DailyReportResults
              data={reportData}
              loading={loading}
              paginationState={paginationState}
              handlePaginationChange={handlePaginationChange}
              reportFilters={reportFilters}
            />
          ) : (
            <EmptyState title="No Data Found" subtitle={`No report found for ${selectedDate}.`} />
          )}
        </CardLayout>
      </Box>
    </PageLayout>
  );
};
